import { useCallback, useEffect, useState, Fragment } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, API, fmtUSD, fmtDateTime, fmtDate, inquiryStatusClass } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Plus, Trash2, AlertTriangle } from "lucide-react";
import QuotesPanel from "@/components/admin/QuotesPanel";

const KPI = ({ label, value, sub, testId }) => (
  <div className="card-sharp p-6" data-testid={testId}>
    <div className="label-mono">{label}</div>
    <div className="font-display text-3xl mt-2">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

// Stable refs for recharts props + admin tab list — extracted to prevent inline-prop churn.
const LINE_DOT = { r: 4, fill: "#0047FF" };
const ADMIN_TABS = [
  { value: "overview", label: "Overview" },
  { value: "bookings", label: "Bookings" },
  { value: "inquiries", label: "Inquiries" },
  { value: "services", label: "Services" },
  { value: "subscribers", label: "Subscribers" },
  { value: "quotes", label: "Quotes" },
  { value: "email", label: "Email activity" },
  { value: "stripe", label: "Stripe activity" },
];

const bookingStatusKlass = (s) => {
  if (s === "paid") return "bg-[#ac62ea]/10 text-[#ac62ea] border-[#ac62ea]/30";
  if (s === "free") return "bg-[#0047FF]/10 text-[#0047FF] border-[#0047FF]/30";
  return "bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/30";
};
const bookingStatusLabel = (s) => (s === "awaiting_payment" ? "awaiting payment" : s);

const EVENT_PALETTE = {
  delivered: "bg-green-100 text-green-800",
  opened: "bg-violet-100 text-violet-800",
  unique_opened: "bg-violet-100 text-violet-800",
  click: "bg-blue-100 text-blue-800",
  request: "bg-gray-100 text-gray-700",
  hard_bounce: "bg-red-100 text-red-800",
  soft_bounce: "bg-amber-100 text-amber-800",
  spam: "bg-red-100 text-red-800",
  unsubscribed: "bg-amber-100 text-amber-800",
  deferred: "bg-gray-100 text-gray-700",
  blocked: "bg-red-100 text-red-800",
  invalid_email: "bg-red-100 text-red-800",
};

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_DAY = 86400;
const FORTNIGHT_DAYS = 14;

const relTime = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < SECONDS_PER_MINUTE) return `${Math.max(0, Math.round(diff))}s ago`;
  if (diff < SECONDS_PER_HOUR) return `${Math.round(diff / SECONDS_PER_MINUTE)}m ago`;
  if (diff < SECONDS_PER_DAY) return `${Math.round(diff / SECONDS_PER_HOUR)}h ago`;
  if (diff < SECONDS_PER_DAY * FORTNIGHT_DAYS) return `${Math.round(diff / SECONDS_PER_DAY)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const LastContacted = ({ data, testId }) => {
  if (!data) {
    return <span className="text-xs text-muted-foreground" data-testid={testId}>—</span>;
  }
  const klass = EVENT_PALETTE[data.event] || "bg-gray-100 text-gray-700";
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit ${klass}`}>
        {data.event}
      </span>
      <span className="text-xs text-muted-foreground">{relTime(data.received_at)}</span>
    </div>
  );
};

const ArtifactsPanel = ({ refType, refId }) => {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ title: "", url: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(
    () => api.get(`/admin/artifacts?ref_type=${refType}&ref_id=${refId}`).then((r) => setItems(r.data)).catch(() => {}),
    [refType, refId],
  );
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!draft.title.trim() || !draft.url.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/artifacts", { ref_type: refType, ref_id: refId, title: draft.title, url: draft.url, note: draft.note });
      setDraft({ title: "", url: "", note: "" });
      setShowForm(false);
      load();
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this artifact?")) return;
    await api.delete(`/admin/artifacts/${id}`);
    load();
  };

  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid={`artifacts-${refType}-${refId}`}>
      <div className="flex items-center justify-between">
        <div className="label-mono">Artifacts ({items.length})</div>
        <button
          className="text-xs font-medium text-[#ac62ea] hover:underline"
          onClick={() => setShowForm((v) => !v)}
          data-testid={`artifact-add-toggle-${refId}`}
        >
          {showForm ? "Cancel" : "+ Add artifact"}
        </button>
      </div>

      {showForm && (
        <div className="mt-3 grid sm:grid-cols-3 gap-2" data-testid={`artifact-form-${refId}`}>
          <input
            value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            placeholder="Title (e.g. Revenue diagnostic v1)"
            className="px-3 py-2 rounded-sm border border-border text-xs"
            data-testid={`artifact-title-${refId}`}
          />
          <input
            value={draft.url}
            onChange={(e) => setDraft({ ...draft, url: e.target.value })}
            placeholder="https:// link to file"
            className="px-3 py-2 rounded-sm border border-border text-xs"
            data-testid={`artifact-url-${refId}`}
          />
          <div className="flex gap-2">
            <input
              value={draft.note}
              onChange={(e) => setDraft({ ...draft, note: e.target.value })}
              placeholder="Note (optional)"
              className="flex-1 px-3 py-2 rounded-sm border border-border text-xs"
              data-testid={`artifact-note-${refId}`}
            />
            <Button
              size="sm"
              disabled={busy || !draft.title.trim() || !draft.url.trim()}
              onClick={submit}
              className="rounded-sm bg-[#ac62ea] hover:bg-[#8a48cf]"
              data-testid={`artifact-submit-${refId}`}
            >
              {busy ? "…" : "Save"}
            </Button>
          </div>
        </div>
      )}

      {items.length > 0 && (
        <ul className="mt-3 space-y-2" data-testid={`artifact-list-${refId}`}>
          {items.map((a) => (
            <li key={a.id} className="flex items-start justify-between gap-3 text-xs" data-testid={`artifact-row-${a.id}`}>
              <div className="min-w-0 flex-1">
                <a href={a.url} target="_blank" rel="noreferrer" className="font-medium text-[#0047FF] hover:underline break-all">{a.title}</a>
                {a.note && <div className="text-muted-foreground mt-0.5">{a.note}</div>}
                <div className="text-muted-foreground mt-0.5">{relTime(a.uploaded_at)} · {a.uploaded_by}</div>
              </div>
              <button
                onClick={() => remove(a.id)}
                className="text-muted-foreground hover:text-red-600 text-xs"
                data-testid={`artifact-delete-${a.id}`}
                aria-label="delete artifact"
              >Remove</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const TabBar = ({ tabs, active, onChange }) => (
  <div className="flex gap-6 border-b border-border overflow-x-auto" data-testid="admin-tabs">
    {tabs.map((t) => (
      <button
        key={t.value}
        className="side-tab whitespace-nowrap"
        data-state={active === t.value ? "active" : "inactive"}
        onClick={() => onChange(t.value)}
        data-testid={`admin-tab-${t.value}`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

export default function AdminDashboard() {
  const { user, login, loading } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [services, setServices] = useState([]);
  const [tab, setTab] = useState("overview");

  const [emailEvents, setEmailEvents] = useState([]);
  const [stripePayments, setStripePayments] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [subSearch, setSubSearch] = useState("");
  const [subSource, setSubSource] = useState("");

  // Quote dialog state
  const [quoting, setQuoting] = useState(null);
  const [quote, setQuote] = useState({ title: "", scope: "", amount: "" });

  // Service dialog
  const [newService, setNewService] = useState({ title: "", description: "", duration_minutes: 60, price: 350, category: "session" });
  const [openService, setOpenService] = useState(false);

  const loadAll = useCallback(async () => {
    const [m, b, i, sv] = await Promise.all([
      api.get("/admin/metrics"),
      api.get("/admin/bookings"),
      api.get("/admin/inquiries"),
      api.get("/services"),
    ]);
    setMetrics(m.data);
    setBookings(b.data);
    setInquiries(i.data);
    setServices(sv.data);
  }, []);

  useEffect(() => {
    if (user?.role === "admin") loadAll();
  }, [user, loadAll]);

  const loadSubscribers = useCallback(() => {
    if (user?.role !== "admin") return;
    const params = new URLSearchParams();
    if (subSearch.trim()) params.set("q", subSearch.trim());
    if (subSource.trim()) params.set("source", subSource.trim());
    params.set("limit", "500");
    api.get(`/admin/subscribers?${params.toString()}`).then((r) => setSubscribers(r.data)).catch(() => {});
  }, [user, subSearch, subSource]);

  useEffect(() => {
    if (user?.role !== "admin") return;
    if (tab === "email") {
      api.get("/admin/email-events?limit=200").then((r) => setEmailEvents(r.data)).catch(() => {});
    } else if (tab === "stripe") {
      api.get("/admin/stripe-payments?limit=200").then((r) => setStripePayments(r.data)).catch(() => {});
    } else if (tab === "subscribers") {
      loadSubscribers();
    }
  }, [tab, user, loadSubscribers]);

  if (loading) return <div className="p-10 text-center text-muted-foreground">Loading…</div>;

  if (!user) {
    return <Navigate to="/login?return_to=%2Fadmin" replace data-testid="admin-login-redirect" />;
  }
  if (user.role !== "admin") {
    return (
      <div className="max-w-md mx-auto px-6 py-24 text-center">
        <h1 className="font-display text-4xl tracking-tight">Admin only</h1>
        <p className="text-muted-foreground mt-4">Your account doesn&apos;t have admin access. Contact the consultant if this is unexpected.</p>
      </div>
    );
  }

  const submitQuote = async () => {
    try {
      await api.post(`/admin/inquiries/${quoting.id}/quote`, {
        title: quote.title,
        scope: quote.scope,
        amount: parseFloat(quote.amount),
      });
      toast.success("Quote sent");
      setQuoting(null);
      setQuote({ title: "", scope: "", amount: "" });
      loadAll();
    } catch {
      toast.error("Failed to send quote");
    }
  };

  const createService = async () => {
    try {
      await api.post("/admin/services", { ...newService, duration_minutes: Number(newService.duration_minutes), price: Number(newService.price) });
      toast.success("Service created");
      setOpenService(false);
      setNewService({ title: "", description: "", duration_minutes: 60, price: 350, category: "session" });
      loadAll();
    } catch {
      toast.error("Failed");
    }
  };

  const archiveService = async (id) => {
    await api.delete(`/admin/services/${id}`);
    loadAll();
  };

  // Service catalog integrity: surface rows with missing SKUs or off-schema categories
  // so admins can fix them before the public site renders broken cards.
  const VALID_CATEGORIES = ["session", "package", "retainer", "project"];
  const integrityIssues = services
    .map((s) => {
      const problems = [];
      if (!s.sku || !s.sku.trim()) problems.push("missing SKU");
      if (!s.title || !s.title.trim()) problems.push("missing title");
      if (s.category && !VALID_CATEGORIES.includes(s.category)) {
        problems.push(`category "${s.category}" not in ${VALID_CATEGORIES.join("|")}`);
      }
      return problems.length ? { service: s, problems } : null;
    })
    .filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-dashboard">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-mono">Studio Control</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-tight">Admin</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/insights"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-xs font-mono uppercase tracking-[0.14em] text-foreground hover:border-[#ac62ea] hover:text-[#ac62ea] transition-colors"
            data-testid="admin-nav-insights"
          >
            Insight composer →
          </Link>
          <Link
            to="/admin/agent-agreements"
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-xs font-mono uppercase tracking-[0.14em] text-foreground hover:border-[#ac62ea] hover:text-[#ac62ea] transition-colors"
            data-testid="admin-nav-agent-agreements"
          >
            Agent agreements →
          </Link>
        </div>
      </div>

      {integrityIssues.length > 0 && (
        <div
          className="mt-6 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 px-5 py-4 flex items-start gap-3"
          data-testid="admin-integrity-banner"
        >
          <AlertTriangle className="h-5 w-5 mt-0.5 flex-shrink-0 text-amber-600" />
          <div className="text-sm">
            <div className="font-semibold">Service catalog data-quality issue</div>
            <ul className="mt-1.5 space-y-1">
              {integrityIssues.map((row) => (
                <li key={row.service.id || row.service.title}>
                  <span className="font-medium">{row.service.title || "(untitled)"}</span>{" "}
                  · <span className="text-xs">{row.problems.join("; ")}</span>{" "}
                  <button
                    type="button"
                    onClick={() => archiveService(row.service.id)}
                    className="ml-2 underline text-amber-900 hover:text-amber-700"
                    data-testid={`admin-integrity-archive-${row.service.id}`}
                  >
                    Archive
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <div className="mt-10">
        <TabBar active={tab} onChange={setTab} tabs={ADMIN_TABS} />

        {tab === "overview" && metrics && (
          <div className="mt-8 space-y-6" data-testid="admin-overview">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <KPI label="Revenue (paid)" value={fmtUSD(metrics.total_revenue)} testId="admin-kpi-revenue" />
              <KPI label="Margin" value={fmtUSD(metrics.margin)} sub={`${metrics.margin_pct}%`} testId="admin-kpi-margin" />
              <KPI label="Paid bookings" value={metrics.bookings_count} testId="admin-kpi-bookings" />
              <KPI label="Pending inquiries" value={metrics.pending_inquiries} testId="admin-kpi-inquiries" />
            </div>
            <div className="card-sharp p-6">
              <div className="label-mono">Revenue trend</div>
              <div className="font-display text-xl mt-1">Monthly</div>
              <div className="mt-6 h-72">
                {metrics.monthly.length > 0 ? (
                  <ResponsiveContainer>
                    <LineChart data={metrics.monthly}>
                      <CartesianGrid stroke="#eee" vertical={false} />
                      <XAxis dataKey="month" stroke="#888" fontSize={12} />
                      <YAxis stroke="#888" fontSize={12} tickFormatter={(v) => `$${v / 1000}k`} />
                      <Tooltip formatter={(v) => fmtUSD(v)} />
                      <Line type="monotone" dataKey="value" stroke="#0047FF" strokeWidth={2.5} dot={LINE_DOT} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-muted-foreground">No paid revenue yet.</div>
                )}
              </div>
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div className="mt-8" data-testid="admin-bookings-tab">
            <div className="card-sharp">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-border">
                    <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                      <th className="p-4">Client</th>
                      <th className="p-4">Service</th>
                      <th className="p-4">Submitted</th>
                      <th className="p-4">Amount</th>
                      <th className="p-4">Status</th>
                      <th className="p-4">Last contacted</th>
                      <th className="p-4">Intake</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.length === 0 && <tr><td colSpan="7" className="p-8 text-sm text-muted-foreground text-center">No bookings yet.</td></tr>}
                    {bookings.map((b) => (
                        <Fragment key={b.id}>
                          <tr className="border-b-0 align-top" data-testid={`admin-booking-${b.id}`}>
                            <td className="p-4">{b.client_name}<br /><span className="text-xs text-muted-foreground">{b.client_email}</span></td>
                            <td className="p-4">{b.service_title}</td>
                            <td className="p-4">{fmtDateTime(b.created_at)}</td>
                            <td className="p-4">{b.amount > 0 ? fmtUSD(b.amount) : <span className="text-muted-foreground">—</span>}</td>
                            <td className="p-4">
                              <span className={`px-2 py-0.5 text-xs rounded-sm border ${bookingStatusKlass(b.payment_status)}`}>{bookingStatusLabel(b.payment_status)}</span>
                            </td>
                            <td className="p-4"><LastContacted data={b.last_contacted} testId={`booking-last-contacted-${b.id}`} /></td>
                            <td className="p-4 text-xs text-muted-foreground max-w-sm">
                              <div><b className="text-foreground">{b.intake?.business_name}</b></div>
                              <div className="mt-1"><i>Goals:</i> {b.intake?.business_goals}</div>
                              <div className="mt-1"><i>Challenges:</i> {b.intake?.challenges}</div>
                            </td>
                          </tr>
                          <tr className="border-b border-border last:border-0">
                            <td colSpan="7" className="px-4 pb-4">
                              <ArtifactsPanel refType="booking" refId={b.id} />
                            </td>
                          </tr>
                        </Fragment>
                      ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "inquiries" && (
          <div className="mt-8 space-y-4" data-testid="admin-inquiries-tab">
            {inquiries.length === 0 && <div className="card-sharp p-8 text-sm text-muted-foreground">No inquiries yet.</div>}
            {inquiries.map((i) => (
              <div key={i.id} className="card-sharp p-6" data-testid={`admin-inquiry-${i.id}`}>
                <div className="flex justify-between flex-wrap gap-3">
                  <div>
                    <div className="label-mono">{fmtDate(i.created_at)}</div>
                    <div className="font-display text-xl mt-1">{i.business_name} — {i.client_name}</div>
                    <div className="text-xs text-muted-foreground">{i.client_email} · Budget {i.budget} · {i.timeline}</div>
                  </div>
                  <div className="flex items-start gap-3">
                    <LastContacted data={i.last_contacted} testId={`inquiry-last-contacted-${i.id}`} />
                    <span className={`px-2 py-0.5 h-fit text-xs rounded-sm border ${inquiryStatusClass(i.status)}`}>{i.status}</span>
                  </div>
                </div>
                <div className="mt-4 grid md:grid-cols-3 gap-4 text-sm">
                  <div><div className="label-mono">Goals</div><p className="mt-1 text-muted-foreground">{i.goals}</p></div>
                  <div><div className="label-mono">Deliverables</div><p className="mt-1 text-muted-foreground">{i.deliverables}</p></div>
                  <div>
                    <div className="label-mono">Quote</div>
                    {i.quote ? (
                      <div className="mt-1">
                        <div className="font-medium">{i.quote.title}</div>
                        <div className="text-xs text-muted-foreground">{fmtUSD(i.quote.amount)} · {i.quote.status}</div>
                      </div>
                    ) : (
                      <Button size="sm" className="mt-1 rounded-sm bg-[#0047FF]" onClick={() => setQuoting(i)} data-testid={`send-quote-${i.id}`}>Send quote</Button>
                    )}
                  </div>
                </div>
                <ArtifactsPanel refType="inquiry" refId={i.id} />
              </div>
            ))}
            <Dialog open={!!quoting} onOpenChange={(o) => !o && setQuoting(null)}>
              <DialogContent className="rounded-sm" data-testid="quote-dialog">
                <DialogHeader><DialogTitle className="font-display">Send quote</DialogTitle></DialogHeader>
                <div className="space-y-4 pt-2">
                  <div><Label className="label-mono">Title</Label><Input className="rounded-sm mt-1" value={quote.title} onChange={(e) => setQuote({ ...quote, title: e.target.value })} data-testid="quote-title" /></div>
                  <div><Label className="label-mono">Scope</Label><Textarea className="rounded-sm mt-1 min-h-[100px]" value={quote.scope} onChange={(e) => setQuote({ ...quote, scope: e.target.value })} data-testid="quote-scope" /></div>
                  <div><Label className="label-mono">Amount (USD)</Label><Input type="number" className="rounded-sm mt-1" value={quote.amount} onChange={(e) => setQuote({ ...quote, amount: e.target.value })} data-testid="quote-amount" /></div>
                </div>
                <DialogFooter>
                  <Button onClick={submitQuote} className="rounded-sm bg-[#0047FF]" data-testid="quote-submit">Send quote</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {tab === "services" && (
          <div className="mt-8" data-testid="admin-services-tab">
            <div className="flex justify-end mb-4">
              <Dialog open={openService} onOpenChange={setOpenService}>
                <DialogTrigger asChild>
                  <Button className="rounded-sm bg-[#0A0A0A]" data-testid="new-service-btn"><Plus className="h-4 w-4 mr-2" />New service</Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm">
                  <DialogHeader><DialogTitle className="font-display">Create service</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div><Label className="label-mono">Title</Label><Input className="rounded-sm mt-1" value={newService.title} onChange={(e) => setNewService({ ...newService, title: e.target.value })} data-testid="srv-title" /></div>
                    <div><Label className="label-mono">Description</Label><Textarea className="rounded-sm mt-1" value={newService.description} onChange={(e) => setNewService({ ...newService, description: e.target.value })} data-testid="srv-desc" /></div>
                    <div className="grid grid-cols-3 gap-3">
                      <div><Label className="label-mono">Duration (min)</Label><Input type="number" className="rounded-sm mt-1" value={newService.duration_minutes} onChange={(e) => setNewService({ ...newService, duration_minutes: e.target.value })} data-testid="srv-duration" /></div>
                      <div><Label className="label-mono">Price (USD)</Label><Input type="number" className="rounded-sm mt-1" value={newService.price} onChange={(e) => setNewService({ ...newService, price: e.target.value })} data-testid="srv-price" /></div>
                      <div><Label className="label-mono">Type</Label><Input className="rounded-sm mt-1" value={newService.category} onChange={(e) => setNewService({ ...newService, category: e.target.value })} data-testid="srv-category" /></div>
                    </div>
                  </div>
                  <DialogFooter><Button onClick={createService} className="rounded-sm bg-[#0047FF]" data-testid="srv-create">Create</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {services.map((s) => (
                <div key={s.id} className="card-sharp p-6" data-testid={`service-admin-${s.id}`}>
                  <div className="flex justify-between">
                    <div>
                      <div className="label-mono">{s.category}</div>
                      <div className="font-display text-xl mt-1">{s.title}</div>
                    </div>
                    <button onClick={() => archiveService(s.id)} className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                  </div>
                  <p className="text-sm text-muted-foreground mt-3">{s.description}</p>
                  <div className="mt-4 pt-4 border-t border-border flex justify-between text-sm">
                    <span>{s.duration_minutes} min</span>
                    <span className="font-medium">{fmtUSD(s.price)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "subscribers" && (
          <div className="mt-8" data-testid="admin-subscribers-tab">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
              <div>
                <div className="label-mono">Insights audience</div>
                <h2 className="font-display text-2xl mt-1 tracking-tight">Subscribers</h2>
                <p className="text-sm text-muted-foreground mt-1">Emails captured from the article subscribe form. Sorted by first seen, newest first.</p>
              </div>
              <a
                href={(() => {
                  const p = new URLSearchParams();
                  if (subSearch.trim()) p.set("q", subSearch.trim());
                  if (subSource.trim()) p.set("source", subSource.trim());
                  const qs = p.toString();
                  return `${API}/admin/subscribers.csv${qs ? `?${qs}` : ""}`;
                })()}
                className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
                data-testid="subscribers-export-csv"
              >
                Export CSV
              </a>
            </div>

            <div className="card-sharp p-4 mb-4 grid sm:grid-cols-[1fr,1fr,auto] gap-3 items-end">
              <div>
                <Label className="label-mono" htmlFor="sub-search">Search by email</Label>
                <Input
                  id="sub-search"
                  className="rounded-sm mt-1"
                  placeholder="e.g. acme.com"
                  value={subSearch}
                  onChange={(e) => setSubSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") loadSubscribers(); }}
                  data-testid="subscribers-search-input"
                />
              </div>
              <div>
                <Label className="label-mono" htmlFor="sub-source">Source (exact)</Label>
                <Input
                  id="sub-source"
                  className="rounded-sm mt-1"
                  placeholder="e.g. acceptance-math-2026"
                  value={subSource}
                  onChange={(e) => setSubSource(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") loadSubscribers(); }}
                  data-testid="subscribers-source-input"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={loadSubscribers}
                  className="rounded-full bg-[#0b1e5a] hover:bg-black h-10 px-5"
                  data-testid="subscribers-search-submit"
                >
                  Search
                </Button>
                {(subSearch || subSource) && (
                  <Button
                    variant="outline"
                    onClick={() => { setSubSearch(""); setSubSource(""); }}
                    className="rounded-full h-10 px-4"
                    data-testid="subscribers-search-clear"
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            <div className="card-sharp">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <div className="text-xs text-muted-foreground" data-testid="subscribers-count">
                  {subscribers.length} subscriber{subscribers.length === 1 ? "" : "s"}
                </div>
              </div>
              <table className="w-full text-sm">
                <thead className="text-left border-b border-border">
                  <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                    <th className="p-4">Email</th>
                    <th className="p-4">First seen</th>
                    <th className="p-4">Source</th>
                    <th className="p-4">Last source</th>
                    <th className="p-4 text-right">Submissions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.length === 0 && (
                    <tr>
                      <td colSpan={5} className="p-8 text-center text-muted-foreground" data-testid="subscribers-empty">
                        No subscribers match this filter yet.
                      </td>
                    </tr>
                  )}
                  {subscribers.map((s) => (
                    <tr key={s.email} className="border-b border-border last:border-0" data-testid={`subscriber-row-${s.email}`}>
                      <td className="p-4 font-medium">{s.email}</td>
                      <td className="p-4 text-muted-foreground whitespace-nowrap">{fmtDateTime(s.first_seen_at)}</td>
                      <td className="p-4 text-xs">
                        <span className="inline-block px-2 py-0.5 rounded-full bg-[#ac62ea]/10 text-[#ac62ea] border border-[#ac62ea]/30">
                          {s.source || "—"}
                        </span>
                      </td>
                      <td className="p-4 text-xs text-muted-foreground">{s.last_source || s.source || "—"}</td>
                      <td className="p-4 text-right tabular-nums">{s.submissions || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "quotes" && <QuotesPanel />}

        {tab === "email" && (
          <div className="mt-8" data-testid="admin-email-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-mono">Brevo activity</div>
                <h2 className="font-display text-2xl mt-1 tracking-tight">Email events</h2>
                <p className="text-sm text-muted-foreground mt-1">Latest delivery, open, click and bounce signals from outbound notifications.</p>
              </div>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => api.get("/admin/email-events?limit=200").then((r) => setEmailEvents(r.data))}
                data-testid="email-events-refresh"
              >
                Refresh
              </Button>
            </div>
            <div className="card-sharp">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-border">
                  <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                    <th className="p-4">When</th>
                    <th className="p-4">Event</th>
                    <th className="p-4">Recipient</th>
                    <th className="p-4">Subject</th>
                  </tr>
                </thead>
                <tbody>
                  {emailEvents.length === 0 && (
                    <tr><td colSpan={4} className="p-8 text-center text-muted-foreground" data-testid="email-events-empty">
                      No events yet. Once Brevo starts posting webhooks to <code className="text-xs">/api/webhook/brevo</code>, they&apos;ll appear here.
                    </td></tr>
                  )}
                  {emailEvents.map((ev) => {
                    const klass = EVENT_PALETTE[ev.event] || "bg-gray-100 text-gray-700";
                    return (
                      <tr key={ev.id} className="border-b border-border last:border-0" data-testid={`email-event-row-${ev.id}`}>
                        <td className="p-4 text-muted-foreground whitespace-nowrap">{fmtDateTime(ev.received_at)}</td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${klass}`} data-testid={`email-event-badge-${ev.event}`}>
                            {ev.event}
                          </span>
                        </td>
                        <td className="p-4">{ev.email}</td>
                        <td className="p-4 text-muted-foreground max-w-xs truncate">{ev.subject || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {tab === "stripe" && (
          <div className="mt-8" data-testid="admin-stripe-tab">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-mono">Stripe activity</div>
                <h2 className="font-display text-2xl mt-1 tracking-tight">Webhook events</h2>
                <p className="text-sm text-muted-foreground mt-1">Raw <code className="text-xs">/api/webhook/stripe</code> arrivals. Paid sessions are auto-matched to the awaiting booking by (email, amount, day).</p>
              </div>
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => api.get("/admin/stripe-payments?limit=200").then((r) => setStripePayments(r.data))}
                data-testid="stripe-events-refresh"
              >
                Refresh
              </Button>
            </div>
            <div className="card-sharp">
              <table className="w-full text-sm">
                <thead className="text-left border-b border-border">
                  <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                    <th className="p-4">When</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Customer</th>
                    <th className="p-4">Amount</th>
                    <th className="p-4">Session</th>
                  </tr>
                </thead>
                <tbody>
                  {stripePayments.length === 0 && (
                    <tr><td colSpan={5} className="p-8 text-center text-muted-foreground" data-testid="stripe-events-empty">
                      No Stripe events yet. Once your Stripe account is connected to Google Calendar appointments, paid sessions will appear here.
                    </td></tr>
                  )}
                  {stripePayments.map((p) => {
                    const klass = p.payment_status === "paid"
                      ? "bg-green-100 text-green-800"
                      : "bg-amber-100 text-amber-800";
                    return (
                      <tr key={p.id} className="border-b border-border last:border-0" data-testid={`stripe-payment-row-${p.id}`}>
                        <td className="p-4 text-muted-foreground whitespace-nowrap">{fmtDateTime(p.received_at)}</td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${klass}`}>
                            {p.payment_status}
                          </span>
                        </td>
                        <td className="p-4">{p.customer_email || "—"}</td>
                        <td className="p-4">{p.amount > 0 ? fmtUSD(p.amount) : "—"}</td>
                        <td className="p-4 text-xs text-muted-foreground font-mono truncate max-w-[180px]">{p.session_id}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
